// TerminFarbPicker.tsx — kleine Farbpunkt-Reihe für die individuelle
// Termin-Farbe (Termine-SP6, A10, Entscheidung 65): feste Preset-Palette
// (TERMIN_FARBEN, kein Freifeld) + „keine Farbe" (× ⇒ Art-/Kalenderfarbe
// greift wieder). Geteilt zwischen TerminFormWindow (Edit-Modus) und
// TerminPeekPopover — keine Doppelpflege (Plan 3.7).
//
// Bewusst mit Inline-Styles (wie AttendeeChips), damit die Komponente in
// beiden Portalen (WindowManager-Fenster UND body-Portal des Peeks) identisch
// aussieht — unabhängig vom .cal-rd-/makeStyles-Scope des Aufrufers.

import * as React from "react";
import { Checkmark16Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { REFINED } from "../risikoanalyseStep/tokens";
import { TERMIN_FARBEN } from "./terminBlock";

const wrapStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
};

const dotBase: React.CSSProperties = {
    appearance: "none",
    width: 18,
    height: 18,
    padding: 0,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#fff",
    boxSizing: "border-box",
};

export interface TerminFarbPickerProps {
    /** Aktive Preset-Farbe (Hex) oder null = keine individuelle Farbe. */
    value: string | null;
    /** Auswahl (Hex) bzw. Entfernen (null); nur bei tatsächlicher Änderung. */
    onSelect: (hex: string | null) => void;
    disabled?: boolean;
    style?: React.CSSProperties;
}

export const TerminFarbPicker: React.FC<TerminFarbPickerProps> = ({
    value,
    onSelect,
    disabled = false,
    style,
}) => {
    const aktiv = (value ?? "").toLowerCase();
    return (
        <div style={{ ...wrapStyle, ...style }} role="radiogroup" aria-label="Termin-Farbe">
            {/* „keine Farbe" zuerst (stabile Position): Art-/Kalenderfarbe greift. */}
            <button
                type="button"
                role="radio"
                aria-checked={!aktiv}
                aria-label="Keine Farbe (Art-/Kalenderfarbe)"
                title="Keine Farbe (Art-/Kalenderfarbe)"
                disabled={disabled}
                style={{
                    ...dotBase,
                    background: REFINED.white,
                    color: REFINED.ink2,
                    border: `1px ${!aktiv ? "solid" : "dashed"} ${REFINED.aub200}`,
                    boxShadow: !aktiv ? `0 0 0 2px ${REFINED.aub200}` : "none",
                    cursor: disabled ? "default" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                }}
                onClick={() => {
                    if (!disabled && aktiv) onSelect(null);
                }}
            >
                <Dismiss16Regular />
            </button>
            {TERMIN_FARBEN.map((f) => {
                const gewaehlt = aktiv === f.hex.toLowerCase();
                return (
                    <button
                        key={f.hex}
                        type="button"
                        role="radio"
                        aria-checked={gewaehlt}
                        aria-label={`Farbe ${f.label}`}
                        title={f.label}
                        disabled={disabled}
                        style={{
                            ...dotBase,
                            background: f.hex,
                            border: "1px solid rgba(0,0,0,0.15)",
                            boxShadow: gewaehlt ? `0 0 0 2px #fff, 0 0 0 4px ${f.hex}` : "none",
                            cursor: disabled ? "default" : "pointer",
                            opacity: disabled ? 0.5 : 1,
                        }}
                        onClick={() => {
                            if (!disabled && !gewaehlt) onSelect(f.hex);
                        }}
                    >
                        {gewaehlt && <Checkmark16Regular />}
                    </button>
                );
            })}
        </div>
    );
};

export default TerminFarbPicker;
